import React from "react"
import { Link, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { URL, LINKEDIN_URL, INSTAGRAM_URL, FACEBOOK_URL, TWITTER_URL } from "./constant"
import { supportedLngs } from "../i18n"

export default function Footer() {
  const { t } = useTranslation()
  const { lang } = useParams()
  const currentLang = lang && supportedLngs.includes(lang) ? lang : "en"
  const prefix = `/${currentLang}`

  return (
    <footer className="bg-black text-white">
      <div className="w-full px-6 py-14">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-10">
          {/* Brand */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1 space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <img
                src="/TrackTeam/trackteamlogo.png"
                alt="TrackTeam Logo"
                className="h-10 w-auto"
              />
              <div className="text-2xl font-semibold" style={{ color: '#62329f' }}>TrackTeam</div>
            </div>
            <p className="text-gray-300 text-sm">{t('footer.tagline')}</p>
            <div className="flex items-center gap-3 pt-2">
              <a href="https://apps.apple.com/ae/app/trackteam/id6755401884" target="_blank" rel="noreferrer">
                <img src="/Image/logo_appstore.svg" alt="App Store" className="h-9 w-auto object-contain" loading="lazy" />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.washey.familytrack.trackteam&pcampaignid=web_share" target="_blank" rel="noreferrer">
                <img src="/Image/logo_playstore.svg" alt="Google Play" className="h-9 w-auto object-contain" loading="lazy" />
              </a>
            </div>
          </div>

          {/* Product */}
          <div>
            <h3 className="font-semibold mb-4 text-sm" style={{ color: '#62329f' }}>{t('footer.product')}</h3>
            <ul className="space-y-2.5">
              <li><Link to={`${prefix}/features`} className="text-gray-300 hover:text-white text-sm">{t('nav.features')}</Link></li>
              <li><Link to={`${prefix}/features/live-gps-tracking`} className="text-gray-300 hover:text-white text-sm">{t('footer.liveGps')}</Link></li>
              <li><Link to={`${prefix}/features/attendance-tracking`} className="text-gray-300 hover:text-white text-sm">{t('footer.attendance')}</Link></li>
              <li><Link to={`${prefix}/features/expense-management`} className="text-gray-300 hover:text-white text-sm">{t('footer.expenses')}</Link></li>
              <li><Link to={`${prefix}/features/job-site-alerts`} className="text-gray-300 hover:text-white text-sm">{t('footer.jobSiteAlerts')}</Link></li>
              <li><Link to={`${prefix}/pricing`} className="text-gray-300 hover:text-white text-sm">{t('nav.pricing')}</Link></li>
            </ul>
          </div>

          {/* Industries */}
          <div>
            <h3 className="font-semibold mb-4 text-sm" style={{ color: '#62329f' }}>{t('nav.industries')}</h3>
            <ul className="space-y-2.5">
              <li><Link to={`${prefix}/industries/construction`} className="text-gray-300 hover:text-white text-sm">{t('footer.construction')}</Link></li>
              <li><Link to={`${prefix}/industries/hvac-plumbing`} className="text-gray-300 hover:text-white text-sm">{t('footer.hvac')}</Link></li>
              <li><Link to={`${prefix}/industries/delivery-logistics`} className="text-gray-300 hover:text-white text-sm">{t('footer.delivery')}</Link></li>
              <li><Link to={`${prefix}/industries/field-sales`} className="text-gray-300 hover:text-white text-sm">{t('footer.fieldSales')}</Link></li>
              <li><Link to={`${prefix}/industries/cleaning-maintenance`} className="text-gray-300 hover:text-white text-sm">{t('footer.cleaning')}</Link></li>
              <li><Link to={`${prefix}/industries/security-services`} className="text-gray-300 hover:text-white text-sm">{t('footer.security')}</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-semibold mb-4 text-sm" style={{ color: '#62329f' }}>{t('footer.company')}</h3>
            <ul className="space-y-2.5">
              <li><Link to={`${prefix}/about`} className="text-gray-300 hover:text-white text-sm">{t('nav.about')}</Link></li>
              <li><Link to={`${prefix}/contact`} className="text-gray-300 hover:text-white text-sm">{t('nav.contact')}</Link></li>
              <li><Link to={`${prefix}/compare/trackteam-vs-hubstaff`} className="text-gray-300 hover:text-white text-sm">vs Hubstaff</Link></li>
              <li><Link to={`${prefix}/compare/trackteam-vs-connecteam`} className="text-gray-300 hover:text-white text-sm">vs Connecteam</Link></li>
            </ul>
            <h3 className="font-semibold mt-6 mb-4 text-sm" style={{ color: '#62329f' }}>{t('footer.legal')}</h3>
            <ul className="space-y-2.5">
              <li><Link to={`${prefix}/privacy-policy`} className="text-gray-300 hover:text-white text-sm">{t('footer.privacyPolicy')}</Link></li>
              <li><Link to={`${prefix}/terms-of-service`} className="text-gray-300 hover:text-white text-sm">{t('footer.termsOfService')}</Link></li>
            </ul>
          </div>
        </div>

        {/* Social + Copyright */}
        <div className="border-t border-white/10 mt-10 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-300 text-sm">{t('footer.copyright')}</p>
          <div className="flex items-center gap-3">
            <a href={TWITTER_URL} target="_blank" rel="noreferrer" className="w-9 h-9 bg-white/10 rounded-md flex items-center justify-center hover:bg-white/20 transition-colors">
              <img src="/twitter-logo.svg" alt="X" className="w-4 h-4 brightness-0 invert block object-contain" loading="lazy" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
              <span className="hidden text-white text-sm font-semibold">X</span>
            </a>
            <a href={FACEBOOK_URL} target="_blank" rel="noreferrer" className="w-9 h-9 bg-white/10 rounded-md flex items-center justify-center hover:bg-white/20 transition-colors">
              <img src="/facebook-logo.svg" alt="Facebook" className="w-4 h-4 brightness-0 invert block object-contain" loading="lazy" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
              <span className="hidden text-white text-sm font-semibold">f</span>
            </a>
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="w-9 h-9 bg-white/10 rounded-md flex items-center justify-center hover:bg-white/20 transition-colors">
              <img src="/instagram-logo.svg" alt="Instagram" className="w-4 h-4 brightness-0 invert block object-contain" loading="lazy" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
              <span className="hidden text-white text-sm font-semibold">IG</span>
            </a>
            <a href={LINKEDIN_URL} target="_blank" rel="noreferrer" className="w-9 h-9 bg-white/10 rounded-md flex items-center justify-center hover:bg-white/20 transition-colors">
              <img src="/linkedin-logo.svg" alt="LinkedIn" className="w-4 h-4 brightness-0 invert block object-contain" loading="lazy" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
              <span className="hidden text-white text-sm font-semibold">in</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
